/**
 * Utilidades para as **ações** do nó de chat (`activitySendMessage` /
 * `activitySendOptions`).
 *
 * Ações são botões de _call-to-action_ que levam a algo **externo** (abrir uma
 * URL, discar um telefone, enviar um e-mail, copiar um texto/código) e que, ao
 * contrário das opções (`opcoes`), **não retornam ao fluxo**: não criam portas
 * de saída (`route-*`), não geram `resposta`/`index` e não passam por
 * `getOptionIdx`. Por isso não precisam de identidade estável para os edges — o
 * `id` serve apenas como key de render/edição no editor, e reordenar não quebra
 * nada.
 *
 * Onde o canal (ou o tipo de ação) não suportar um botão nativo, o backend faz
 * fallback anexando as ações como texto no fim da mensagem via
 * `formatarAcoesComoTexto`.
 */

export type AcaoTipo = "link" | "telefone" | "email" | "copiar";

/**
 * Forma mínima de uma ação para render/fallback — sem `id`. É o que trafega no
 * transporte (`UserMessageAction` no backend). `Acao` (com `id`) é o formato do
 * editor; ambos satisfazem esta interface, então os helpers aceitam os dois.
 */
export type AcaoConteudo = {
  tipo: AcaoTipo;
  label: string;
  conteudo: string;
};

export type Acao = AcaoConteudo & {
  id: string;
};

/** Rótulo padrão por tipo, usado quando a ação não tem `label` preenchido. */
const LABEL_PADRAO: Record<AcaoTipo, string> = {
  link: "Link",
  telefone: "Telefone",
  email: "E-mail",
  copiar: "Copiar",
};

/**
 * Gera um id curto e único para uma ação. Prefixo não-numérico só por
 * consistência com `gerarOpcaoId`; aqui não há rota posicional para colidir.
 */
export function gerarAcaoId(usados?: Set<string>): string {
  let id: string;
  do {
    id = "a" + Math.random().toString(36).slice(2, 9);
  } while (usados && usados.has(id));
  usados?.add(id);
  return id;
}

/** `true` se a ação tem tipo e conteúdo preenchidos (mínimo para ser enviada). */
export function isAcaoValida<T extends AcaoConteudo>(acao: T | null | undefined): acao is T {
  return (
    !!acao &&
    typeof acao.conteudo === "string" &&
    acao.conteudo.trim().length > 0 &&
    (acao.tipo === "link" ||
      acao.tipo === "telefone" ||
      acao.tipo === "email" ||
      acao.tipo === "copiar")
  );
}

/** Texto exibível de uma ação (usa o rótulo padrão do tipo se `label` vazio). */
export function acaoLabel(acao: AcaoConteudo): string {
  const label = (acao.label ?? "").trim();
  return label.length ? label : LABEL_PADRAO[acao.tipo] ?? "";
}

/**
 * `href` navegável de uma ação, quando aplicável:
 * - `link` → a própria URL (garante esquema http/https)
 * - `telefone` → `tel:<numero-sem-formatacao>`
 * - `email` → `mailto:<endereco>`
 * - `copiar` → `null` (não é um alvo navegável)
 */
export function acaoHref(acao: AcaoConteudo): string | null {
  const conteudo = (acao.conteudo ?? "").trim();
  if (!conteudo) return null;

  switch (acao.tipo) {
    case "link":
      return /^[a-z][a-z0-9+.-]*:\/\//i.test(conteudo) ? conteudo : `https://${conteudo}`;
    case "telefone":
      return `tel:${conteudo.replace(/[^\d+]/g, "")}`;
    case "email":
      return conteudo.toLowerCase().startsWith("mailto:") ? conteudo : `mailto:${conteudo}`;
    case "copiar":
    default:
      return null;
  }
}

/**
 * Renderiza uma lista de ações como texto, uma por linha, no formato
 * `"{label}: {conteudo}"`. Usado como fallback pelos canais/tipos que não
 * suportam botão nativo. Ignora ações inválidas.
 */
export function formatarAcoesComoTexto(acoes: AcaoConteudo[] | null | undefined): string {
  if (!acoes?.length) return "";
  return acoes
    .filter(isAcaoValida)
    .map((acao) => `${acaoLabel(acao)}: ${acao.conteudo.trim()}`)
    .join("\n");
}
